'use client'
import { getTimeFromNow } from '@/utils/date-time'
import { Box, BoxProps, Stack, StackProps, Theme, Typography, TypographyProps } from '@mui/material'
import { styled, SxProps } from '@mui/material/styles'
import Image from 'next/image'
import Link from '@/features/ref/components/Link'

const StyledBlogImage = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  borderRadius: 8,
  overflow: 'hidden',
  flexShrink: 0,
  '& img': {
    transition: 'transform 0.3s ease-in-out',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  '&:hover img': {
    transform: 'scale(1.05)',
  },
}))

const StyledBlogTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  lineHeight: 1.5,
  // color: theme.palette.secondary.main,
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  transition: 'color 0.3s ease-in-out',
  '&:hover': {
    color: theme.palette.primary.main,
    cursor: 'pointer',
  },
}))

type BlogItemProps = {
  blog: IBlogPost
  sx?: SxProps<Theme>
  blogImageProps?: Omit<BoxProps, 'children'>
  blogTitleProps?: Omit<TypographyProps, 'children'>
  blogContentProps?: Omit<StackProps, 'children'> & { hideDescription?: boolean }
} & Omit<BoxProps, 'children' | 'sx'>

export const BlogItem = ({
  blog,
  sx,
  blogImageProps,
  blogTitleProps,
  blogContentProps,
  ...otherBoxProps
}: BlogItemProps) => {
  const { hideDescription, ...restContentProps } = blogContentProps || { hideDescription: false }
  return (
    <Box
      key={blog.url_key}
      sx={{
        display: 'flex',
        gap: 1.5,
        borderRadius: 1,
        ...sx,
      }}
      {...otherBoxProps}
    >
      <StyledBlogImage {...blogImageProps}>
        <Link href={`/blog/${blog?.url_key}`}>
          <Image
            sizes="(max-width: 600px) 100vw, (max-width: 1400px) 50vw, 33vw"
            src={blog?.image}
            alt={blog.name}
            width={756}
            height={425}
            quality={90}
          />
        </Link>
      </StyledBlogImage>

      <Stack {...restContentProps} spacing={1} sx={{ flex: 1, minWidth: 0, ...blogContentProps?.sx }}>
        <StyledBlogTitle
          variant="h4"
          {...blogTitleProps}
          sx={{
            fontWeight: 500,
            mb: 1,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontSize: '1em',
            ...blogTitleProps?.sx,
          }}
        >
          <Link href={`/blog/${blog?.url_key}`}>{blog?.name}</Link>
        </StyledBlogTitle>

        <Stack direction="row" spacing={{ sm: 0.3, xs: 0.3 }} alignItems="center">
          <Box sx={{ width: { sm: 15 }, aspectRatio: '1/1', flexShrink: 0 }}>
            <Image src={'/assets/icons/favicon.png'} alt={blog?.author_name} width={24} height={24} />
          </Box>

          <Stack direction="row" spacing={0.2} alignItems="center" sx={{ flex: 1 }}>
            <Typography
              sx={(theme) => ({
                fontSize: 14,
                fontWeight: 600,
                // color: theme.palette.secondary.main,
              })}
            >
              {blog?.author_name}
            </Typography>
            <Box sx={{ width: '.8em', height: '.8em' }}>
              <img src="/assets/icons/verified.png" alt="verified" width={16} height={16} />
            </Box>
          </Stack>

          <Typography
            variant="caption"
            sx={{
              fontStyle: 'italic',
              color: '#616161',
              lineHeight: 1.5,
            }}
          >
            {getTimeFromNow(blog?.publish_date)}
          </Typography>
        </Stack>
        {blog?.short_description && !blogContentProps?.hideDescription && (
          <Typography variant="body2">{blog?.short_description}</Typography>
        )}
      </Stack>
    </Box>
  )
}
